import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Table } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { dashboarddata } from "../../../../../services/AdminService";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
} from "recharts";
import { FaUsers } from "react-icons/fa";

// Color constants
const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

const CARD_COLORS = {
    total: "#3498db",
    subscribed: "#2ecc71",
    unsubscribed: "#e74c3c",
    envelopes: "#9b59b6",
    documents: "#f1c40f",
    pending: "#e67e22",
    completed: "#27ae60",
    daily: "#1abc9c",
    weekly: "#f39c12",
    monthly: "#9b59b6",
};

// Role ID to Label Mapping
const roleLabelMap = {
    1: "Admin",
    2: "Owner",
    3: "Top",
    4: "Medium",
    5: "Low",
};

// Main Dashboard Component
const TotalUserDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [timeFilter, setTimeFilter] = useState("all");

    const [data, setData] = useState({
        totalUsers: 0,
        dailyActiveUsers: 0,
        weeklyActiveUsers: 0,
        monthlyActiveUsers: 0,
        recentusers: [],
        plandistribution: [
            { plan_id: 1, plan_name: "Plan A", user_count: 0 },
            { plan_id: 2, plan_name: "Plan B", user_count: 0 },
        ],
        roleDistribution: [
            { role_id: "Admin", user_count: 0 },
            { role_id: "User", user_count: 0 },
        ],
    });

    // Fetch data with filter
    useEffect(() => {
        setLoading(true);
        dashboarddata({ timePeriod: timeFilter })
            .then((response) => {
                const apiData = response.data;

                setData({
                    totalUsers: Number(apiData.total_users),
                    roleDistribution: apiData.role_distribution.map((role) => ({
                        role_id: roleLabelMap[role.role_id] || role.role_id, // Map role_id to labels
                        TotalUser: Number(role.user_count)   // Ensure user_count is a number
                    })),
                    plandistribution: apiData.plan_distribution.map((plan) => ({
                        plan_id: plan.plan_id,
                        plan_name: plan.plan_name,
                        user: plan.user_count
                    })),
                    recentusers: apiData.recent_users.map((users) => ({
                        id: users.id,
                        first_name: users.first_name,
                        last_name: users.last_name,
                        company_name: users.company_name,
                        email: users.email,
                        mobile: users.mobile,
                        created_at: users.created_at,
                    })),
                    dailyActiveUsers: Number(apiData.daily_active_users),
                    weeklyActiveUsers: Number(apiData.weekly_active_users),
                    monthlyActiveUsers: Number(apiData.monthly_active_users),
                });

                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching dashboard data:", error);
                setLoading(false);
            });
    }, [timeFilter]);

    const activeUsersData = [
        { name: "Daily", value: data.dailyActiveUsers },
        { name: "Weekly", value: data.weeklyActiveUsers },
        { name: "Monthly", value: data.monthlyActiveUsers },
    ];

    const {
        totalUsers,
        plandistribution,
        roleDistribution,
        recentusers,
        dailyActiveUsers,
        weeklyActiveUsers,
        monthlyActiveUsers,
    } = data;

    return (
        <>
            <div className="custom_container px-0">
                {loading && (
                    <div className="page-loading">
                        <img src="/images/loader.gif" alt="loader" />
                    </div>
                )}
                <Container className="rounded-3 mt-4" fluid style={{ backgroundColor: "#f5f6fa", minHeight: "100vh", padding: "20px" }}>

                    {/* Header with Filter */}
                    <Row className="mb-4 d-flex justify-content-between align-items-center">
                        <Col>
                            <h2 style={{ color: "#2c3e50", fontWeight: "bold" }}>Admin Dashboard</h2>
                        </Col>
                    </Row>

                    {/* Users Section */}
                    <section className="mb-5">
                        <h3 className="mb-2" style={{ color: "#34495e", marginBottom: "20px" }}>Users Overview</h3>
                        <Row xs={1} sm={2} md={4} className="g-4">
                            <StatCard title="Total Users" value={totalUsers} icon={<FaUsers />} bgColor={CARD_COLORS.total} />
                            {plandistribution.map((plan) => (
                                <StatCard
                                    key={plan.plan_id}
                                    title={plan.plan_name}
                                    value={plan.user}
                                    icon={<FaUsers />}
                                    bgColor={CARD_COLORS.subscribed}
                                />
                            ))}
                        </Row>
                    </section>

                    {/* Analytics Section */}
                    <section className="mb-5">
                        <h3 className="mb-2" style={{ color: "#34495e", marginBottom: "50px" }}>Analytics</h3>
                        <Row xs={1} md={2} className="g-4">
                            <Col>
                                <Card style={{ height: "400px", padding: "20px", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>
                                    <h4 className="text-center mb-4" style={{ color: "#2c3e50" }}>Role Distribution</h4>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={roleDistribution}>
                                            <XAxis dataKey="role_id" stroke="#666" /> {/* Use mapped role_id labels */}
                                            <YAxis stroke="#666" />
                                            <Tooltip formatter={(value) => `${value} `} /> {/* Format tooltip */}
                                            <Bar dataKey="TotalUser" fill="#007bff" barSize={40} /> {/* Use user_count for bar heights */}
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>
                        </Row>
                    </section>

                    {/* Recent Users Table */}
                    <Table>
                        <thead>
                            <tr>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Company Name</th>
                                <th>Email</th>
                                <th>Mobile</th>
                                <th>Create Time</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentusers.map((users) => (
                                <tr key={users.id}>
                                    <td>{users.first_name}</td>
                                    <td>{users.last_name}</td>
                                    <td>{users.company_name}</td>
                                    <td>{users.email}</td>
                                    <td>{users.mobile}</td>
                                    <td>
                                        {new Date(users.created_at).toLocaleString("en-GB", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric",
                                            minute: "2-digit",
                                            hour: "2-digit",
                                            hour12: true,
                                        })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {/* Active Users Section */}
                    <section className="mb-5">
                        <h3 className="mb-2" style={{ color: "#34495e", marginBlock: "20px" }}>Active Users Overview</h3>
                        <Row xs={1} sm={2} md={3} className="g-4">
                            <StatCard title="Daily Active Users" value={dailyActiveUsers} icon={<FaUsers />} bgColor={CARD_COLORS.daily} />
                            <StatCard title="Weekly Active Users" value={weeklyActiveUsers} icon={<FaUsers />} bgColor={CARD_COLORS.weekly} />
                            <StatCard title="Monthly Active Users" value={monthlyActiveUsers} icon={<FaUsers />} bgColor={CARD_COLORS.monthly} />
                        </Row>
                    </section>

                    {/* Charts Section */}
                    {/* <section className="mb-5">
                        <Row xs={1} md={3} className="g-4">
                            <Col>
                                <Card style={{ height: "400px", padding: "20px" }}>
                                    <h4 className="text-center">Active Users Pie Chart</h4>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={activeUsersData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                                                {activeUsersData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>
                            <Col>
                                <Card style={{ height: "400px", padding: "20px" }}>
                                    <h4 className="text-center">Active Users Bar Chart</h4>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={activeUsersData}>
                                            <XAxis dataKey="name" stroke="#666" />
                                            <YAxis stroke="#666" />
                                            <Tooltip formatter={(value) => `${value} users`} />
                                            <Bar dataKey="value" fill="#8884d8" barSize={40} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>
                            <Col>
                                <Card style={{ height: "400px", padding: "20px" }}>
                                    <h4 className="text-center">Active Users Line Chart</h4>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={activeUsersData}>
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Line type="monotone" dataKey="value" stroke="#8884d8" strokeWidth={2} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </Card>
                            </Col>
                        </Row>
                    </section> */}
                </Container>
            </div>
        </>
    );
};

// StatCard Component
const StatCard = ({ title, value, icon, bgColor }) => (
    <Col>
        <Card style={{ backgroundColor: bgColor, color: "#fff", height: "150px", borderRadius: "10px" }}>
            <Card.Body className="d-flex flex-column justify-content-center align-items-center">
                <div className="mb-2">{icon}</div>
                <Card.Title style={{ fontSize: "1.1rem" }}>{title}</Card.Title>
                <Card.Text style={{ fontSize: "2rem", fontWeight: "bold" }}>{value}</Card.Text>
            </Card.Body>
        </Card>
    </Col>
);

export default TotalUserDashboard;